import { Component, ReactNode } from "react";
import { resolveAppLang } from "@/lib/detect-lang";
import { Sentry } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, errorId: null };

  static getDerivedStateFromError(): State {
    const errorId = Math.random().toString(36).substring(2, 8).toUpperCase();
    return { hasError: true, errorId };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error(`[ErrorBoundary ${this.state.errorId}]`, error, info.componentStack);
    Sentry.captureException(error, {
      contexts: { react: { componentStack: info.componentStack ?? undefined } },
      tags: { errorId: this.state.errorId ?? undefined },
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, errorId: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      const isEs = resolveAppLang() === "es";
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          <div className="glass-card rounded-lg p-8 text-center space-y-4 max-w-md">
            <h1 className="text-2xl font-serif text-primary">
              {isEs ? "Algo salió mal" : "Something went wrong"}
            </h1>
            <p className="text-muted-foreground font-light">
              {isEs
                ? "Ha ocurrido un error inesperado. Puedes intentar de nuevo o recargar la página."
                : "An unexpected error occurred. You can try again or reload the page."}
            </p>
            {this.state.errorId && (
              <p className="text-2xs text-muted-foreground/70 font-mono">
                REF: {this.state.errorId}
              </p>
            )}
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={this.handleReset}
                className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors font-medium"
              >
                {isEs ? "Intentar de nuevo" : "Try again"}
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 border border-border text-muted-foreground rounded-md hover:text-foreground transition-colors font-light"
              >
                {isEs ? "Recargar" : "Reload"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
